import { useEffectOnLoad } from "helpers/hooks/useEffectOnLoad";
import { GetParamCaseInsensitive, isNullOrWhiteSpace } from "helpers/objects";
import { IESignatureOAuthStateQueryParam } from "models/IESignature";
import React, { useCallback, useMemo } from 'react';
import { useSearchParams } from "react-router-dom";
import ESignatureAPI from "./ESignatureAPI";
import useWait from "helpers/context/Page/useWait";
import { ESignatureRedirectSourceEnum } from "helpers/enums";
import { useTranslations } from "@fenetech/translations";

const ESignatureCallback: React.FC = () => {

    const [query] = useSearchParams();
    const wait = useWait();
    const tm = useTranslations();

    const basePath = useMemo(() => {
        return document.getElementsByTagName("base")[0].href;
    }, []);

    const authCode: string | null = useMemo(() => {
        return GetParamCaseInsensitive(query, "code") ?? null;
    }, [query]);

    const state: IESignatureOAuthStateQueryParam | null = useMemo(() => {
        const stateParam = GetParamCaseInsensitive(query, "state") ?? null;
        if (stateParam) {
            const decoded = atob(decodeURIComponent(stateParam));
            const objResult = JSON.parse(decoded);
            //Backend deserialized properties are pascal, ensure they map properly
            let res: IESignatureOAuthStateQueryParam = {
                customerGUID: objResult.CustomerGUID,
                redirectSource: objResult.RedirectSource,
                oKey: objResult.OKey
            }
            return res;
        }

        return null;
    }, [query]);

    const resolveCallbackURI = useCallback((state: IESignatureOAuthStateQueryParam) => {
        let callbackURI: string = basePath;

        switch (state.redirectSource) {
            case ESignatureRedirectSourceEnum.Setup:
                callbackURI += `setup/esign/?customerGUID=${state.customerGUID}`;
                break;
            case ESignatureRedirectSourceEnum.Maintenance:
                callbackURI += "maintenance";
                break;
            case ESignatureRedirectSourceEnum.Quote:
                callbackURI += `quotes/?oKey=${state.oKey!.toString()}`;
                break;
        }

        return callbackURI;
    }, [basePath]);

    const generateTokensAndRedirect = useCallback(async (authCode: string, state: IESignatureOAuthStateQueryParam) => {
        const tokenResponse = await ESignatureAPI.GenerateTokens(state.customerGUID, authCode);
        let callbackURI = resolveCallbackURI(state);

        if (!isNullOrWhiteSpace(tokenResponse.message)) {
            callbackURI += `&eSignMessage=${encodeURI(tm.Get(tokenResponse.message))}`
        }

        window.location.assign(callbackURI);
    }, [resolveCallbackURI, tm]);

    useEffectOnLoad(() => {
        if (!isNullOrWhiteSpace(authCode) && state) {
            generateTokensAndRedirect(authCode!, state)
        }
    });

    wait.Show(true);

    return <></>;
}

export default ESignatureCallback;